/**
 * @file AppHeader.tsx
 * @description Содержимое верхней шапки admin-кабинета (phase08.5 — shadcn).
 *
 * Mobile-first:
 *  - `< md`: hamburger слева (открывает Sheet-sidebar), справа — Avatar-меню.
 *  - `≥ md`: hamburger скрыт, справа — identity-widget + ContextSwitcher + Avatar + Logout.
 *
 * phase19.29 (кабинет платформы) — необязательные пропсы, без них шапка
 * прежняя (кабинет магазина их не передаёт):
 *  - `start` — слева после гамбургера: место под название и пояснение
 *    страницы (`cabinet-header-slots`);
 *  - `end` — справа: место под главное действие страницы;
 *  - `hideUser` — блок пользователя (кто я, смена кабинета, аватар-меню,
 *    «Выйти») не рисуется: в кабинете платформы он живёт внизу боковой
 *    панели (`SidebarUserMenu`).
 */

import { ArrowLeftRight, LogOut, Menu as MenuIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { formatContextLabel } from "../auth/format";
import { useAuth } from "../auth/hooks";
import { ContextSwitcher } from "./ContextSwitcher";
import { CurrentIdentityWidget } from "./CurrentIdentityWidget";
import { useAppShellOptional } from "./ui/AppShell";
import { Avatar } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface AppHeaderProps {
  /** Место под название страницы (слева). */
  start?: ReactNode;
  /** Место под главное действие страницы (справа). */
  end?: ReactNode;
  /** Не показывать блок пользователя — он в боковой панели. */
  hideUser?: boolean;
}

export function AppHeader({ start, end, hideUser = false }: AppHeaderProps = {}) {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const shell = useAppShellOptional();
  const isMobile = shell?.isMobile ?? false;
  const [menuOpen, setMenuOpen] = useState(false);

  async function onLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  if (!me) return null;

  const displayName = me.user.displayName ?? me.user.email;
  const contextLabel = formatContextLabel(me.activeContext);

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className={start ? "flex min-w-0 flex-1 items-center gap-1" : "flex items-center gap-1"}>
        {shell?.menuInDrawer ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={shell.toggleSidebar}
            aria-label="Открыть меню"
            data-testid="sidebar-toggle"
          >
            <MenuIcon className="size-5" />
          </Button>
        ) : null}
        {start}
      </div>

      <div className={start ? "flex shrink-0 items-center gap-2 md:gap-4" : "flex items-center gap-2 md:gap-4"}>
        {end}
        {!hideUser && !isMobile ? (
          <>
            <CurrentIdentityWidget user={me.user} activeContext={me.activeContext} />
            <ContextSwitcher />
          </>
        ) : null}

        {hideUser ? null : (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger
              render={(props) => (
                <button
                  {...props}
                  type="button"
                  aria-label="Профиль пользователя"
                  data-testid="profile-avatar-button"
                  className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring transition-shadow"
                >
                  <Avatar name={displayName} size={32} data-testid="profile-avatar" />
                </button>
              )}
            />
            <DropdownMenuContent align="end" sideOffset={6} className="min-w-60">
              <DropdownMenuLabel className="normal-case tracking-normal">
                <div className="text-sm font-medium text-on-surface truncate">
                  {displayName}
                </div>
                <div className="text-xs text-on-surface-variant truncate font-normal">
                  {contextLabel}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isMobile && me.contexts.length > 1 ? (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/select-context");
                  }}
                >
                  <ArrowLeftRight className="size-4" />
                  Сменить контекст
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => void onLogout()}>
                <LogOut className="size-4" />
                Выйти
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!hideUser && !isMobile ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void onLogout()}
          >
            <LogOut className="size-4" />
            Выйти
          </Button>
        ) : null}
      </div>
    </div>
  );
}
